import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { usePublicMeta } from "@/lib/publicMeta";
import { emitChange, MESSAGES_CHANGED } from "@/lib/events";
import { verifyPushSubscription } from "@/lib/push";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfirmProvider } from "@/components/Confirm";
import { TenantProvider, useTenant } from "@/context/TenantContext";
import { SettingsProvider } from "@/context/SettingsContext";
import {
  AdminRoute,
  OwnerRoute,
  ProtectedRoute,
  SignedInRoute,
} from "@/components/Bits";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SetPassword from "@/pages/SetPassword";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import Accounts from "@/pages/Accounts";
import Overview from "@/pages/Overview";
import Platform from "@/pages/Platform";
import Support from "@/pages/Support";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import Messages from "@/pages/Messages";
import Profile from "@/pages/Profile";
import MyTickets from "@/pages/MyTickets";
import Landing from "@/pages/Landing";

/** Two things the service worker tells this tab about, both from
 * service-worker.js: clicking a push notification focuses this tab but
 * can't itself change the SPA's route, so it posts a message instead and
 * this turns that into a navigate(); and a push landing at all (whether or
 * not it's ever clicked) re-fires MESSAGES_CHANGED, which is what makes an
 * already-open conversation or unread badge update within about a second
 * of a reply instead of waiting on the next poll.
 *
 * It also silently re-verifies this browser's own push subscription on
 * every load it's signed in for (see verifyPushSubscription's own note on
 * why that matters — a computer's subscription can die without its on/off
 * toggle ever noticing, which is how one machine in an office quietly stops
 * getting the popup while the others keep working). */
function NotificationBridge() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tenantId } = useTenant();
  // Agents/admins are pinned to their own business as soon as they're signed
  // in, so tenantId isn't needed for them — only an owner has to have picked
  // one first, otherwise the subscribe call below 428s until their next load.
  const canSubscribe = !!user && (user.role !== "owner" || !!tenantId);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event) => {
      if (event.data?.type === "notification-click" && event.data.url) {
        navigate(event.data.url);
      } else if (event.data?.type === "push-received") {
        emitChange(MESSAGES_CHANGED);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);

  useEffect(() => {
    if (canSubscribe) verifyPushSubscription();
  }, [canSubscribe]);

  return null;
}

export default function App() {
  // Keeps the tab title/favicon in sync with Platform settings on every
  // screen, not just the sign-in page — someone opening a bookmark straight
  // into /dashboard never renders <Login>, so this can't live there alone.
  usePublicMeta();

  return (
    <BrowserRouter>
      <ConfirmProvider>
      <AuthProvider>
        <TenantProvider>
          <SettingsProvider>
            <NotificationBridge />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-password" element={<SetPassword />} />

              {/* Platform owner only */}
              <Route element={<OwnerRoute />}>
                <Route path="/overview" element={<Overview />} />
                <Route path="/accounts" element={<Accounts />} />
                <Route path="/platform" element={<Platform />} />
                <Route path="/support-inbox" element={<Support />} />
              </Route>

              {/* Inside a client account */}
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/support" element={<MyTickets />} />
              </Route>

              {/* Anyone signed in, client selected or not */}
              <Route element={<SignedInRoute />}>
                <Route path="/profile" element={<Profile />} />
              </Route>

              <Route element={<AdminRoute />}>
                <Route path="/settings" element={<Settings />} />
                <Route path="/team" element={<Team />} />
              </Route>

              <Route path="/" element={<Landing />} />
              <Route path="*" element={<Landing />} />
            </Routes>
            {/*
              Top right, but brief — it does sit over the page's main action, so
              it needs to clear itself quickly rather than waiting to be closed.
            */}
            <Toaster position="top-right" richColors closeButton duration={2000} />
          </SettingsProvider>
        </TenantProvider>
      </AuthProvider>
      </ConfirmProvider>
    </BrowserRouter>
  );
}
